import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    Tooltip,
    Menu,
    MenuItem,
    Avatar,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LanguageIcon from '@mui/icons-material/Language';
import QuizIcon from '@mui/icons-material/Quiz';
import { useNavigation } from '../../contexts/NavigationContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoLight from '../../assets/Logo OK.png';
import LogoDark from '../../assets/image.png';
import LogoMiniLight from '../../assets/logo-mini-preto.png';
import LogoMiniDark from '../../assets/logo-mini-branco.png';
import { alpha } from '@mui/material/styles';
import { AccountCircle } from '@mui/icons-material';

const drawerWidth = 240;

const DashboardLayout = ({ children, toggleColorMode }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(() => {
        const savedState = localStorage.getItem('menuOpen');
        return savedState === null ? true : savedState === 'true';
    });
    const { currentPage, navigateTo } = useNavigation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [userInfo, setUserInfo] = useState(JSON.parse(localStorage.getItem('userInfo') || '{}'));
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleStorageChange = () => {
            setUserInfo(JSON.parse(localStorage.getItem('userInfo') || '{}'));
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Sincroniza o currentPage com a URL atual
    useEffect(() => {
        const path = location.pathname;
        console.log('URL atual:', path);
        
        // Mapeia a URL para o item de menu correspondente
        if (path.includes('/flows-new')) {
            if (currentPage !== 'flows-new') {
                navigateTo('flows-new', true);
            }
        } else if (path.includes('/question-bank')) {
            if (currentPage !== 'question-bank') {
                navigateTo('question-bank', true);
            }
        } else if (path.includes('/history')) {
            if (currentPage !== 'historico') {
                navigateTo('historico', true);
            }
        } else if (path.includes('/reports')) {
            if (currentPage !== 'relatorios') {
                navigateTo('relatorios', true);
            }
        } else if (path.includes('/users')) {
            if (currentPage !== 'usuarios') {
                navigateTo('usuarios', true);
            }
        } else if (path.includes('/global-variable-mappings')) {
            if (currentPage !== 'global-variable-mappings') {
                navigateTo('global-variable-mappings', true);
            }
        } else if (path === '/') {
            if (currentPage !== 'historico') {
                navigateTo('historico', true);
            }
        }
    }, [location.pathname, currentPage, navigateTo]);

    const menuItems = [
        {
            text: 'Histórico',
            icon: <HistoryIcon />,
            page: 'historico'
        },
        {
            text: 'Fluxos',
            icon: <AccountTreeIcon />,
            page: 'flows-new',
            path: '/flows-new',
            adminOnly: true
        },
        {
            text: 'Banco de Questões',
            icon: <QuizIcon />,
            page: 'question-bank',
            path: '/question-bank',
            adminOnly: true
        },
        {
            text: 'Relatórios',
            icon: <AssignmentIcon />,
            page: 'relatorios'
        },
        {
            text: 'Usuários',
            icon: <PeopleIcon />,
            page: 'usuarios',
            adminOnly: true
        },
        {
            text: 'Mapeamentos Globais',
            icon: <LanguageIcon />,
            page: 'global-variable-mappings',
            path: '/global-variable-mappings',
            adminOnly: true
        },
        {
            text: 'Configurações',
            icon: <SettingsIcon />,
            page: 'configuracoes',
            adminOnly: true
        }
    ];

    const filteredMenuItems = menuItems.filter(item => 
        !item.adminOnly || (item.adminOnly && userInfo.is_admin)
    );

    const handleDrawerToggle = () => {
        const newState = !open;
        setOpen(newState);
        localStorage.setItem('menuOpen', newState.toString());
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        navigate('/login');
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProfile = () => {
        handleClose();
        navigateTo('perfil');
    };

    const getAvatarUrl = () => {
        if (userInfo.avatar_url) {
            return `${process.env.REACT_APP_API_URL}${userInfo.avatar_url}`;
        }
        return null;
    };

    const handleItemClick = (item) => {
        navigateTo(item.page);
        if (item.path) {
            navigate(item.path);
        }
    };

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: theme.zIndex.drawer - 1,
                    transition: theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    ...(open && {
                        marginLeft: drawerWidth,
                        width: `calc(100% - ${drawerWidth}px)`,
                        transition: theme.transitions.create(['width', 'margin'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    }),
                    backgroundColor: theme.palette.mode === 'dark' 
                        ? '#1E1E1E'
                        : '#FFFFFF',
                    borderBottom: `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.1)'}`
                }}
            >
                <Toolbar>
                    <Box sx={{ width: '72px' }} />
                    <Typography 
                        variant="h6" 
                        noWrap 
                        component="div" 
                        sx={{ 
                            flexGrow: 1,
                            textAlign: 'center',
                            mr: 10
                        }}
                    >
                        Guia de competência territorial e-notariado
                    </Typography>
                    <Tooltip title="Alternar tema claro/escuro">
                        <IconButton onClick={toggleColorMode} color="inherit">
                            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        onClick={handleMenu}
                        color="inherit"
                    >
                        <Avatar 
                            src={getAvatarUrl()}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '2px solid',
                                borderColor: theme.palette.primary.main
                            }}
                        >
                            {userInfo.username ? userInfo.username[0].toUpperCase() : 'U'}
                        </Avatar>
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="permanent"
                open={open}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                    '& .MuiDrawer-paper': {
                        zIndex: theme.zIndex.drawer + 2,
                        overflow: 'visible',
                        backgroundColor: theme.palette.mode === 'dark' 
                            ? '#1E1E1E'
                            : '#FFFFFF',
                        borderRight: `1px solid ${theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.1)'}`,
                    },
                    ...(open && {
                        ...{
                            width: drawerWidth,
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.enteringScreen,
                            }),
                            '& .MuiDrawer-paper': {
                                width: drawerWidth,
                                overflowX: 'hidden',
                                overflow: 'visible',
                                transition: theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }),
                            },
                        },
                    }),
                    ...(!open && {
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                        width: theme.spacing(7),
                        [theme.breakpoints.up('sm')]: {
                            width: theme.spacing(9),
                        },
                        '& .MuiDrawer-paper': {
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: theme.transitions.duration.leavingScreen,
                            }),
                            overflowX: 'hidden',
                            overflow: 'visible',
                            width: theme.spacing(7),
                            [theme.breakpoints.up('sm')]: {
                                width: theme.spacing(9),
                            },
                        },
                    }),
                }}
            >
                <IconButton
                    onClick={handleDrawerToggle}
                    sx={{
                        position: 'absolute',
                        right: -16,
                        top: 12,
                        width: 32,
                        height: 32,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '50%',
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)',
                        },
                        '& .MuiSvgIcon-root': {
                            fontSize: 20,
                            transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
                            transition: 'transform 0.3s ease',
                        },
                    }}
                >
                    <MenuIcon />
                </IconButton>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 2,
                        mb: 1,
                    }}
                >
                    <Box
                        component="img"
                        src={open 
                            ? (theme.palette.mode === 'dark' ? LogoDark : LogoLight)
                            : (theme.palette.mode === 'dark' ? LogoMiniDark : LogoMiniLight)
                        }
                        alt="Logo"
                        sx={{
                            width: open ? '180px' : '40px',
                            height: 'auto',
                            transition: 'width 0.3s ease',
                        }}
                    />
                </Box>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: '100%',
                    overflowX: 'hidden'
                }}>
                    <List>
                        {filteredMenuItems.map((item) => (
                            <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                                <ListItemButton
                                    selected={currentPage === item.page}
                                    onClick={() => handleItemClick(item)}
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: open ? 'initial' : 'center',
                                        px: 2.5,
                                        '&.Mui-selected': {
                                            backgroundColor: theme.palette.mode === 'dark' 
                                                ? 'rgba(255, 255, 255, 0.08)'
                                                : 'rgba(0, 0, 0, 0.04)',
                                            '&:hover': {
                                                backgroundColor: theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.12)'
                                                    : 'rgba(0, 0, 0, 0.08)',
                                            },
                                        },
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.02)',
                                        }
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 3 : 'auto',
                                            justifyContent: 'center',
                                            color: currentPage === item.page ? 'primary.main' : 'inherit',
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={item.text} 
                                        sx={{ 
                                            opacity: open ? 1 : 0,
                                            color: currentPage === item.page ? 'primary.main' : 'inherit',
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    <Box sx={{ flexGrow: 1 }} />
                    <Divider />
                    <List>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                            <ListItemButton
                                onClick={handleProfile}
                                sx={{
                                    minHeight: 48,
                                    justifyContent: open ? 'initial' : 'center',
                                    px: 2.5,
                                    transition: 'all 0.3s ease',
                                    borderRadius: 1,
                                    mx: 1,
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(211, 47, 47, 0.2)'
                                            : 'rgba(211, 47, 47, 0.1)',
                                        '& .MuiListItemIcon-root, & .MuiTypography-root': {
                                            color: '#d32f2f',
                                        }
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                        transition: 'color 0.3s ease',
                                        color: theme.palette.mode === 'dark' 
                                            ? 'rgba(211, 47, 47, 0.7)'
                                            : 'rgba(211, 47, 47, 0.6)',
                                    }}
                                >
                                    <PersonIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Perfil" 
                                    sx={{ 
                                        opacity: open ? 1 : 0,
                                        transition: 'all 0.3s ease',
                                        '& .MuiTypography-root': {
                                            color: theme.palette.mode === 'dark' 
                                                ? 'rgba(211, 47, 47, 0.7)'
                                                : 'rgba(211, 47, 47, 0.6)',
                                            fontWeight: 500,
                                        }
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'block' }}>
                            <ListItemButton
                                onClick={handleLogout}
                                sx={{
                                    minHeight: 48,
                                    justifyContent: open ? 'initial' : 'center',
                                    px: 2.5,
                                    transition: 'all 0.3s ease',
                                    borderRadius: 1,
                                    mx: 1,
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? 'rgba(211, 47, 47, 0.2)'
                                            : 'rgba(211, 47, 47, 0.1)',
                                        '& .MuiListItemIcon-root, & .MuiTypography-root': {
                                            color: '#d32f2f',
                                        }
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                        transition: 'color 0.3s ease',
                                        color: theme.palette.mode === 'dark' 
                                            ? 'rgba(211, 47, 47, 0.7)'
                                            : 'rgba(211, 47, 47, 0.6)',
                                    }}
                                >
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText 
                                    primary="Sair" 
                                    sx={{ 
                                        opacity: open ? 1 : 0,
                                        transition: 'all 0.3s ease',
                                        '& .MuiTypography-root': {
                                            color: theme.palette.mode === 'dark' 
                                                ? 'rgba(211, 47, 47, 0.7)'
                                                : 'rgba(211, 47, 47, 0.6)',
                                            fontWeight: 500,
                                        }
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>
            <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <MenuItem onClick={handleProfile}>
                    <ListItemIcon>
                        <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Perfil</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Sair</ListItemText>
                </MenuItem>
            </Menu>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 2,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: 0,
                    mt: '64px',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default DashboardLayout; 